import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  MessageSquare,
  Send,
  ExternalLink,
  MapPin,
  Phone,
  Clock,
  CheckCircle2,
  AlertCircle,
  Linkedin,

  Globe,
  Users,
  BookOpen,
  Sparkles
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

// Contact card component
const ContactCard = ({ icon: Icon, title, children, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
  >
    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <h3 className="font-semibold text-gray-800 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Social link component
const SocialLink = ({ href, icon: Icon, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors group"
  >
    <div className="w-10 h-10 bg-gray-100 group-hover:bg-primary group-hover:text-white rounded-full flex items-center justify-center transition-all">
      <Icon className="w-5 h-5" />
    </div>
    <span className="text-gray-700 group-hover:text-primary transition-colors">{label}</span>
    <ExternalLink className="w-4 h-4 text-gray-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
  </a>
);

const Contacts = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false
  });
  const [sending, setSending] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setFormStatus({ type: '', message: '' });

    const formPayload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formPayload.append(key, key === 'consent' ? (value ? 'Yes' : 'No') : value);
    });
    formPayload.append('access_key', '41df86bc-2a63-4cca-9c0f-514628ac59b4');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formPayload
      });

      const data = await response.json();

      if (data.success) {
        setFormStatus({
          type: 'success',
          message: 'Thank you for your message! We will get back to you soon.'
        });
        setFormData({ name: '', email: '', subject: '', message: '', consent: false });
      } else {
        setFormStatus({
          type: 'error',
          message: data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'An error occurred. Please try again later.'
      });
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    {
      q: "Is the AI(R) Poetry Generator free to use?",
      a: "Yes! Our poetry generator is completely free and requires no registration."
    },
    {
      q: "What data sources do you use?",
      a: "We use historical air pollution data from monitoring stations in Bergamo and Treviglio, Italy."
    },
    {
      q: "Can I use the generated poems?",
      a: "Yes, you're free to use, share, and publish the poems generated by our platform."
    },
    {
      q: "How can I contribute to the project?",
      a: "We welcome collaborations! Contact us through this form to discuss partnership opportunities."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">

          <div className="absolute inset-0 bg-white/90" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium mb-6 border border-gray-200">
              Contact Us
            </span>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
              Let's Start a<br />
              <span className="text-primary">Conversation</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about the AI(R) Poetry Generator? Interested in collaboration?
              We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 -mt-16 relative z-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <ContactCard icon={Mail} title="Email Us" delay={0}>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Research & Academic</p>
                  <a href="mailto:stefano.rozzoni@unibg.it" className="text-primary hover:underline">
                    stefano.rozzoni@unibg.it
                  </a>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Technical Support</p>
                  <a href="mailto:contact@airpoetrygenerator.com" className="text-primary hover:underline">
                    contact@airpoetrygenerator.com
                  </a>
                </div>
              </div>
            </ContactCard>

            <ContactCard icon={MapPin} title="Our Locations" delay={0.1}>
              <div className="space-y-3 text-gray-600 text-sm">
                <div>
                  <p className="font-medium text-gray-800">University of Bergamo</p>
                  <p>Department of Literature & Philosophy</p>
                  <p>Bergamo, Italy</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Digital Humanities Lab</p>
                  <p>Bangalore, India</p>
                </div>
              </div>
            </ContactCard>

            <ContactCard icon={Clock} title="Response Time" delay={0.2}>
              <p className="text-gray-600 text-sm mb-3">
                We typically respond within 24-48 hours during business days.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Currently accepting inquiries</span>
              </div>
            </ContactCard>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-primary/90 p-6 text-white">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-serif font-bold">Send a Message</h2>
                      <p className="text-primary-100 text-sm">We'd love to hear from you</p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  {formStatus.message && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-start gap-3 p-4 rounded-lg mb-6 ${formStatus.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                    >
                      {formStatus.type === 'success' ? (
                        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      )}
                      <p>{formStatus.message}</p>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-gray-700">Name *</Label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full mt-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-gray-700">Email *</Label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full mt-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-gray-700">Subject *</Label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full mt-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        placeholder="How can we help?"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-700">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="w-full mt-1 p-3 min-h-[150px] border-gray-200 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                        className="mt-1"
                      />
                      <Label htmlFor="consent" className="text-sm text-gray-600 font-normal leading-relaxed">
                        I agree to have my feedback published on the website and understand my data will be handled
                        in accordance with privacy guidelines. (Optional)
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full py-4 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-lg transition-all"
                    >
                      {sending ? (
                        <span className="flex items-center justify-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <Send className="w-5 h-5" />
                          </motion.div>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Send className="w-5 h-5" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Column - Connect & FAQ */}
            <div className="space-y-8">
              {/* Connect Section */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="border-0 shadow-xl">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-serif font-bold text-gray-800 mb-6 flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Connect With Us
                    </h3>
                    <div className="space-y-2">

                      <SocialLink
                        href="https://www.researchgate.net/"
                        icon={BookOpen}
                        label="ResearchGate"
                      />
                      <SocialLink
                        href="https://www.nestresearchnetwork.org"
                        icon={Globe}
                        label="NEST Research Network"
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* FAQ Section */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="border-0 shadow-xl">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-serif font-bold text-gray-800 mb-6 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Frequently Asked Questions
                    </h3>
                    <div className="space-y-4">
                      {faqs.map((faq, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="border-b border-gray-100 pb-4 last:border-0 last:pb-0"
                        >
                          <h4 className="font-medium text-gray-800 mb-2">{faq.q}</h4>
                          <p className="text-gray-600 text-sm">{faq.a}</p>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Contacts;
